import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

// Mirrors UpdaterState in apps/desktop-electron/src/main/updater.ts.
// Kept as a standalone type so the desktop app never imports from the
// Electron workspace (keeps the web build clean).
export type UpdaterState =
  | { kind: "idle"; currentVersion: string; lastChecked: number | null }
  | { kind: "checking"; currentVersion: string }
  | { kind: "not-available"; currentVersion: string; lastChecked: number }
  | {
      kind: "available";
      currentVersion: string;
      availableVersion: string;
      releaseDate: string | null;
    }
  | {
      kind: "downloading";
      currentVersion: string;
      availableVersion: string;
      percent: number;
      bytesPerSecond: number;
      transferred: number;
      total: number;
    }
  | {
      kind: "downloaded";
      currentVersion: string;
      availableVersion: string;
      releaseDate: string | null;
    }
  | { kind: "error"; currentVersion: string; message: string };

interface InstallResult {
  started: boolean;
  reason?: string;
}

interface UpdaterApi {
  getStatus(): Promise<UpdaterState>;
  checkNow(): Promise<void>;
  installNow(): Promise<InstallResult | void>;
  onEvent(cb: (state: UpdaterState) => void): () => void;
}

// If the app is still alive this long after invoking installNow, the install
// didn't take effect (Squirrel.Mac silent failure, stuck tray-hide, etc.).
const INSTALL_TIMEOUT_MS = 10_000;

function getUpdaterApi(): UpdaterApi | null {
  const api = (
    window as unknown as { __electronApi?: { updater?: UpdaterApi } }
  ).__electronApi?.updater;
  return api ?? null;
}

export interface UseElectronUpdaterResult {
  /** Null when running outside Electron (web / Tauri during transition). */
  state: UpdaterState | null;
  checkNow: () => void;
  installNow: () => void;
}

/**
 * Subscribes to auto-updater state from the Electron main process.
 * No-op on non-Electron builds.
 */
export function useElectronUpdater(): UseElectronUpdaterResult {
  const [state, setState] = useState<UpdaterState | null>(null);

  useEffect(() => {
    const api = getUpdaterApi();
    if (!api) return;

    let cancelled = false;
    // If a pushed event arrives before the initial getStatus resolves, don't
    // let the older status overwrite the newer state.
    let receivedPushedEvent = false;

    api.getStatus().then((initial) => {
      if (cancelled || receivedPushedEvent) return;
      setState(initial);
    });

    const unsubscribe = api.onEvent((next) => {
      receivedPushedEvent = true;
      setState(next);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const checkNow = useCallback(() => {
    getUpdaterApi()?.checkNow();
  }, []);

  const installNow = useCallback(() => {
    const api = getUpdaterApi();
    if (!api) return;
    toast.message("Restarting to install update…");
    const timeout = window.setTimeout(() => {
      // If we got here, the main process didn't actually quit. Either
      // Squirrel.Mac silently refused to swap the bundle (unsigned,
      // /Applications mismatch, running from the DMG), or something is
      // holding the window open. Tell the user what to do.
      toast.error(
        "Couldn't install automatically. Quit Ano and reopen it to finish updating.",
      );
    }, INSTALL_TIMEOUT_MS);
    api
      .installNow()
      .then((res) => {
        if (res && !res.started) {
          window.clearTimeout(timeout);
          toast.error(
            res.reason === "not-downloaded"
              ? "Update isn't ready yet. Try again in a moment."
              : `Install failed: ${res.reason ?? "unknown error"}`,
          );
        }
      })
      .catch(() => {
        window.clearTimeout(timeout);
        toast.error("Install request didn't reach the desktop shell.");
      });
  }, []);

  return { state, checkNow, installNow };
}
